import type { DirectiveBinding, ObjectDirective } from 'vue'

type LoadFn = () => void | Promise<void>

export type ScrollLoadBindingValue = {
  load: LoadFn
  disabled?: boolean
  distance?: number
}

type ScrollLoadContext = {
  load: LoadFn
  disabled: boolean
  distance: number
  loading: boolean
  wrap?: HTMLElement | null
  autocomplete?: HTMLElement | null
  handler?: (event: Event) => void
  focusHandler?: () => void
  rafId?: number
  ensureWrap?: () => void
}

const CONTEXT_KEY = Symbol('scroll-load-context')

const DEFAULT_DISTANCE = 10

type BindingValue = ScrollLoadBindingValue | LoadFn

const resolveBinding = (binding: DirectiveBinding<BindingValue>) => {
  const value = binding.value
  if (typeof value === 'function') {
    return {
      load: value as LoadFn,
      disabled: false,
      distance: DEFAULT_DISTANCE,
    }
  }

  if (value && typeof value === 'object' && 'load' in value) {
    const distance =
      typeof value.distance === 'number' && value.distance >= 0
        ? value.distance
        : DEFAULT_DISTANCE

    return {
      load: (value.load ?? (() => {})) as LoadFn,
      disabled: Boolean(value.disabled),
      distance,
    }
  }

  return {
    load: () => {},
    disabled: true,
    distance: DEFAULT_DISTANCE,
  }
}

const setContext = (el: HTMLElement, ctx: ScrollLoadContext) => {
  Reflect.set(el, CONTEXT_KEY, ctx)
}

const getContext = (el: HTMLElement): ScrollLoadContext | undefined => {
  return Reflect.get(el, CONTEXT_KEY)
}

const cleanup = (el: HTMLElement) => {
  const ctx = getContext(el)
  if (!ctx) return

  if (ctx.wrap && ctx.handler) {
    ctx.wrap.removeEventListener('scroll', ctx.handler)
  }

  if (ctx.focusHandler) {
    el.removeEventListener('focusin', ctx.focusHandler)
    el.removeEventListener('mouseenter', ctx.focusHandler)
  }

  if (typeof ctx.rafId === 'number') {
    cancelAnimationFrame(ctx.rafId)
  }

  Reflect.deleteProperty(el, CONTEXT_KEY)
}

const resolveAutocompleteRoot = (el: HTMLElement): HTMLElement | null => {
  if (el.getAttribute('aria-owns')) return el
  return el.querySelector('[aria-owns]') as HTMLElement | null
}

const findWrap = (el: HTMLElement, ctx: ScrollLoadContext) => {
  const autocomplete = resolveAutocompleteRoot(el)
  ctx.autocomplete = autocomplete
  if (!autocomplete) return null

  const listboxId = autocomplete.getAttribute('aria-owns')
  if (!listboxId) return null

  const scrollRoot = document.getElementById(listboxId)
  if (!scrollRoot) return null

  return scrollRoot.querySelector(
    '.hep-autocomplete-suggestion__wrap',
  ) as HTMLElement | null
}

const scrollLoad: ObjectDirective<HTMLElement, BindingValue> = {
  mounted(el, binding) {
    const { load, disabled, distance } = resolveBinding(binding)

    const ctx: ScrollLoadContext = {
      load,
      disabled,
      distance,
      loading: false,
    }

    ctx.handler = () => {
      if (ctx.disabled || ctx.loading) return
      const wrap = ctx.wrap
      if (!wrap) return

      const threshold = Math.max(ctx.distance, 0)
      const reachedBottom =
        wrap.scrollTop + wrap.clientHeight + threshold >= wrap.scrollHeight

      if (!reachedBottom) return

      ctx.loading = true

      Promise.resolve(ctx.load())
        .catch(() => undefined)
        .finally(() => {
          ctx.loading = false
        })
    }

    const ensureWrap = () => {
      if (ctx.wrap && ctx.wrap.isConnected) return
      const wrap = findWrap(el, ctx)
      if (!wrap) {
        ctx.rafId = requestAnimationFrame(ensureWrap)
        return
      }

      if (ctx.wrap && ctx.wrap !== wrap && ctx.handler) {
        ctx.wrap.removeEventListener('scroll', ctx.handler)
      }

      ctx.wrap = wrap
      if (ctx.handler) {
        wrap.addEventListener('scroll', ctx.handler)
      }
    }

    ctx.ensureWrap = ensureWrap

    const focusHandler = () => {
      if (typeof ctx.rafId === 'number') {
        cancelAnimationFrame(ctx.rafId)
      }
      ensureWrap()
    }

    ctx.focusHandler = focusHandler

    el.addEventListener('focusin', focusHandler)
    el.addEventListener('mouseenter', focusHandler)

    setContext(el, ctx)
    ensureWrap()
  },
  updated(el, binding) {
    const ctx = getContext(el)
    if (!ctx) return

    const { load, disabled, distance } = resolveBinding(binding)
    ctx.load = load
    ctx.disabled = disabled
    ctx.distance = distance

    if (!ctx.wrap || !ctx.wrap.isConnected) {
      ctx.ensureWrap?.()
    }
  },
  beforeUnmount(el) {
    cleanup(el)
  },
}

export default scrollLoad
